import { useSyncExternalStore } from "react";

export const REUSABLE_SKILL_SUGGESTIONS_STORAGE_KEY =
  "bustly:preferences:show-reusable-skill-suggestions";

const REUSABLE_SKILL_SUGGESTIONS_CHANGED_EVENT =
  "bustly:reusable-skill-suggestions-preference-changed";
const DEFAULT_REUSABLE_SKILL_SUGGESTIONS_ENABLED = true;

type ReadableStorage = Pick<Storage, "getItem">;

type ReusableSkillSuggestionsPreferenceState = {
  enabled: boolean;
  disabledAt: number | null;
};

let volatileState: ReusableSkillSuggestionsPreferenceState | undefined;
let legacyDisabledAt: number | undefined;

function parseStoredValue(
  value: string | null,
): ReusableSkillSuggestionsPreferenceState | undefined {
  if (value === "true") {
    return { enabled: true, disabledAt: null };
  }
  if (value === "false") {
    legacyDisabledAt ??= Date.now();
    return { enabled: false, disabledAt: legacyDisabledAt };
  }
  if (!value) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(value) as {
      enabled?: unknown;
      disabledAt?: unknown;
    };
    if (typeof parsed.enabled !== "boolean") {
      return undefined;
    }
    if (parsed.enabled) {
      return { enabled: true, disabledAt: null };
    }
    if (
      typeof parsed.disabledAt !== "number" ||
      !Number.isFinite(parsed.disabledAt) ||
      parsed.disabledAt < 0
    ) {
      return undefined;
    }
    return { enabled: false, disabledAt: parsed.disabledAt };
  } catch {
    return undefined;
  }
}

function resolveBrowserStorage(): Storage | null {
  if (
    typeof window === "undefined" ||
    typeof window.localStorage === "undefined"
  ) {
    return null;
  }
  return window.localStorage;
}

function readReusableSkillSuggestionsPreferenceState(
  storage: ReadableStorage | null = resolveBrowserStorage(),
): ReusableSkillSuggestionsPreferenceState {
  try {
    const storedState = parseStoredValue(
      storage?.getItem(REUSABLE_SKILL_SUGGESTIONS_STORAGE_KEY) ?? null,
    );
    if (storedState !== undefined) {
      return storedState;
    }
  } catch {
    // Some embedded hosts can deny storage access. Keep the preference usable in-memory.
  }
  return (
    volatileState ?? {
      enabled: DEFAULT_REUSABLE_SKILL_SUGGESTIONS_ENABLED,
      disabledAt: null,
    }
  );
}

export function readReusableSkillSuggestionsPreference(
  storage: ReadableStorage | null = resolveBrowserStorage(),
): boolean {
  return readReusableSkillSuggestionsPreferenceState(storage).enabled;
}

export function readReusableSkillSuggestionsDisabledAt(
  storage: ReadableStorage | null = resolveBrowserStorage(),
): number | null {
  return readReusableSkillSuggestionsPreferenceState(storage).disabledAt;
}

export function setReusableSkillSuggestionsPreference(enabled: boolean): void {
  volatileState = {
    enabled,
    disabledAt: enabled ? null : Date.now(),
  };
  const storage = resolveBrowserStorage();
  try {
    storage?.setItem(
      REUSABLE_SKILL_SUGGESTIONS_STORAGE_KEY,
      JSON.stringify(volatileState),
    );
  } catch {
    // The in-memory value above still keeps both controls synchronized in this renderer.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(REUSABLE_SKILL_SUGGESTIONS_CHANGED_EVENT));
  }
}

function subscribeReusableSkillSuggestionsPreference(
  onStoreChange: () => void,
): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== REUSABLE_SKILL_SUGGESTIONS_STORAGE_KEY) {
      return;
    }
    volatileState = parseStoredValue(event.newValue) ?? {
      enabled: DEFAULT_REUSABLE_SKILL_SUGGESTIONS_ENABLED,
      disabledAt: null,
    };
    onStoreChange();
  };
  window.addEventListener(
    REUSABLE_SKILL_SUGGESTIONS_CHANGED_EVENT,
    onStoreChange,
  );
  window.addEventListener("storage", handleStorage);
  return () => {
    window.removeEventListener(
      REUSABLE_SKILL_SUGGESTIONS_CHANGED_EVENT,
      onStoreChange,
    );
    window.removeEventListener("storage", handleStorage);
  };
}

export function useReusableSkillSuggestionsPreference(): [
  enabled: boolean,
  setEnabled: (enabled: boolean) => void,
] {
  const enabled = useSyncExternalStore(
    subscribeReusableSkillSuggestionsPreference,
    readReusableSkillSuggestionsPreference,
    () => DEFAULT_REUSABLE_SKILL_SUGGESTIONS_ENABLED,
  );
  return [enabled, setReusableSkillSuggestionsPreference];
}

export function useReusableSkillSuggestionsDisabledAt(): number | null {
  return useSyncExternalStore(
    subscribeReusableSkillSuggestionsPreference,
    readReusableSkillSuggestionsDisabledAt,
    () => null,
  );
}
